<!-- src/apps/secret/components/dashboard/DashboardView.vue -->

<script setup lang="ts">
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useLocalReceiptStore } from '@/shared/stores/localReceiptStore';
  import { type LocalReceipt } from '@/types/ui/local-receipt';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import DashboardReceiptsTable from './DashboardReceiptsTable.vue';
  import SecretComposer from './SecretComposer.vue';

  /**
   * The authenticated Dashboard (Figma Make, ago/2026), split into two tabs:
   *   - "Segredos": the composer — create + send secrets (800px column).
   *   - "Enviados": the receipts ledger, given a much wider column so the full
   *     email / phone / filenames breathe.
   *
   * Rendered by BOTH entry points into the signed-in home so the two cannot
   * drift: the workspace `/dashboard` route and HomepageContent's
   * `authenticated` branch (custom-domain / guard-bypass paths).
   */

  const { t } = useI18n();

  const bootstrapStore = useBootstrapStore();
  const { ui } = storeToRefs(bootstrapStore);

  const localReceiptStore = useLocalReceiptStore();

  type Tab = 'secrets' | 'sent';
  const activeTab = ref<Tab>('secrets');

  // Bumping the key remounts the ledger so it re-fetches after a creation,
  // the same refresh handshake DashboardIndex used with RecentSecretsTable.
  const ledgerKey = ref(0);
  const onCreated = (_receipt: LocalReceipt) => {
    ledgerKey.value += 1;
  };

  // Total sent count, surfaced by the ledger for the tab badge.
  const sentCount = ref(0);

  // The "Enviados" tab gets a wide column; "Segredos" keeps the mock's 800px.
  const containerWidth = computed(() =>
    activeTab.value === 'sent' ? 'max-w-[1280px]' : 'max-w-[800px]'
  );

  const heading = computed(() =>
    activeTab.value === 'sent'
      ? { title: t('dashboard.page_title_sent'), intro: t('dashboard.page_intro_sent') }
      : { title: t('dashboard.page_title'), intro: t('dashboard.page_intro') }
  );

  const tabs = computed<{ id: Tab; label: string }[]>(() => [
    { id: 'secrets', label: t('dashboard.tab_secrets') },
    { id: 'sent', label: t('dashboard.tab_sent') },
  ]);
</script>

<template>
  <div
    class="mx-auto w-full min-w-[320px] px-5 pt-[52px] pb-20 transition-[max-width] duration-200"
    :class="containerWidth">
    <header class="mb-6">
      <div class="mb-3 flex items-center gap-2">
        <span
          class="inline-block size-1.5 shrink-0 rounded-full bg-brand-600 shadow-[0_0_6px_var(--color-brand-500)] dark:bg-brand-500"
          aria-hidden="true"></span>
        <span
          class="font-mono text-[10px] font-semibold tracking-[0.14em] text-brand-600 uppercase dark:text-brand-500">
          {{ t('dashboard.page_eyebrow') }}
        </span>
      </div>
      <h1 class="font-brand text-2xl font-bold tracking-[-0.01em] text-day-ink sm:text-[28px] dark:text-night-ink">
        {{ heading.title }}
      </h1>
      <p class="mt-2 max-w-xl font-mono text-[11px] leading-relaxed tracking-[0.02em] text-day-ink-muted dark:text-night-ink-muted">
        {{ heading.intro }}
      </p>
    </header>

    <!-- Tab bar: a filled segmented control so the active view reads loud and
         the inactive one still looks clickable (not a faded hairline). -->
    <div
      class="mb-8 inline-flex gap-1 rounded-lg border border-day-line bg-day-100 p-1 shadow-[inset_0_1px_2px_rgba(13,21,55,0.04)] dark:border-white/[0.13] dark:bg-night-200 dark:shadow-none"
      role="tablist"
      :aria-label="t('dashboard.page_eyebrow')">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="activeTab === tab.id"
        class="flex items-center gap-2 rounded-md px-4 py-2 font-mono text-xs font-semibold tracking-[0.06em] uppercase transition-all duration-150 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none"
        :class="
          activeTab === tab.id
            ? 'bg-brand-600 text-white shadow-sm dark:bg-brand-500'
            : 'text-day-ink-muted hover:bg-day-200/60 hover:text-day-ink dark:text-night-ink-muted dark:hover:bg-white/[0.06] dark:hover:text-night-ink'
        "
        :data-testid="`dashboard-tab-${tab.id}`"
        @click="activeTab = tab.id">
        <svg
          class="size-4 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <template v-if="tab.id === 'secrets'">
            <rect
              x="3"
              y="11"
              width="18"
              height="11"
              rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </template>
          <template v-else>
            <path d="M22 2 11 13" />
            <path d="M22 2 15 22l-4-9-9-4 20-7z" />
          </template>
        </svg>
        {{ tab.label }}
        <span
          v-if="tab.id === 'sent' && sentCount > 0"
          class="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold"
          :class="activeTab === tab.id ? 'bg-white/25 text-white' : 'bg-brand-500 text-white'">
          {{ sentCount }}
        </span>
      </button>
    </div>

    <!-- Segredos: the composer. v-show (not v-if) so its inline generated link
         and "copy now" warning survive a trip to the Enviados tab and back. -->
    <div
      v-show="activeTab === 'secrets'"
      role="tabpanel">
      <SecretComposer
        v-if="ui?.enabled !== false"
        :workspace-mode="localReceiptStore.workspaceMode"
        @created="onCreated" />
    </div>

    <!-- Enviados: the receipts ledger, full width. -->
    <div
      v-show="activeTab === 'sent'"
      role="tabpanel">
      <DashboardReceiptsTable
        :key="ledgerKey"
        @count="sentCount = $event" />
    </div>
  </div>
</template>
